import { useState, useCallback, useRef, useMemo } from 'react';
import { Upload, FileText, Download, FileDown, X, AlertCircle, RectangleHorizontal, RectangleVertical, RotateCcw } from 'lucide-react';
import { parseArsTxt } from '../utils/arsTxtParser';
import { parseArsHtml } from '../utils/arsHtmlParser';
import { serializeToArsTxt } from '../utils/arsTxtSerializer';
import { BarDiagramChart } from '../components/barDiagram/BarDiagramChart';
import { BarDiagramInfo } from '../components/barDiagram/BarDiagramInfo';
import { BarDiagramSidePanel } from '../components/barDiagram/BarDiagramSidePanel';
import { BarDiagramChangesPanel } from '../components/barDiagram/BarDiagramChangesPanel';
import type { ArsColorCode, BarDiagramData, DiagramMetadata } from '../types/barDiagram';

const PDF_SERVICE_URL = import.meta.env.DEV
  ? 'http://localhost:8290'
  : '';

export function BarDiagramView() {
  const [data, setData] = useState<BarDiagramData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [activePhaseIndex, setActivePhaseIndex] = useState<number | undefined>();
  const [useRawOffset, setUseRawOffset] = useState(true);
  const [landscape, setLandscape] = useState(false);
  const [maxGroups, setMaxGroups] = useState<16 | 32>(16);
  const [sourceType, setSourceType] = useState<'txt' | 'html' | null>(null);
  const [originalData, setOriginalData] = useState<BarDiagramData | null>(null);
  const [changesPanelCollapsed, setChangesPanelCollapsed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback((file: File) => {
    setError(null);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const isTxt = file.name.toLowerCase().endsWith('.txt');
        const parsed = isTxt ? parseArsTxt(content) : parseArsHtml(content);
        setData(parsed);
        setOriginalData(structuredClone(parsed));
        setSourceType(isTxt ? 'txt' : 'html');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al parsear el archivo');
        setData(null);
        setOriginalData(null);
        setSourceType(null);
      }
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  // --- Mutation callbacks ---

  const handleCellColorChange = useCallback((groupIdx: number, cellIdx: number, color: ArsColorCode) => {
    setData(prev => {
      if (!prev) return prev;
      const newGroups = prev.groups.map((g, gi) => {
        if (gi !== groupIdx) return g;
        const newCells = [...g.cells];
        newCells[cellIdx] = color;
        return { ...g, cells: newCells };
      });
      return { ...prev, groups: newGroups };
    });
  }, []);

  const handleDurationChange = useCallback((phaseIdx: number, duration: number) => {
    setData(prev => {
      if (!prev) return prev;
      const newPhases = prev.phases.map((p, i) => {
        if (i !== phaseIdx) return p;
        return { ...p, duration };
      });
      // Also update plan 0 phaseDurations if it's a phase slot (type 'phase')
      const newPlans = prev.plans.map((plan, pi) => {
        if (pi !== 0) return plan;
        const newPhaseDurations = [...plan.phaseDurations];
        if (newPhaseDurations[phaseIdx] !== null) {
          newPhaseDurations[phaseIdx] = duration;
        }
        return { ...plan, phaseDurations: newPhaseDurations };
      });
      return { ...prev, phases: newPhases, plans: newPlans };
    });
  }, []);

  const handleMetadataChange = useCallback((field: keyof DiagramMetadata, value: string) => {
    setData(prev => {
      if (!prev) return prev;
      return { ...prev, metadata: { ...prev.metadata, [field]: value } };
    });
  }, []);

  // --- Change tracking ---

  const changeCount = useMemo(() => {
    if (!data || !originalData) return { colors: 0, durations: 0, metadata: 0, total: 0 };
    let colors = 0;
    let durations = 0;
    let metadata = 0;

    // Color changes
    for (let gi = 0; gi < data.groups.length; gi++) {
      const orig = originalData.groups[gi];
      const curr = data.groups[gi];
      if (!orig || !curr) continue;
      for (let ci = 0; ci < curr.cells.length; ci++) {
        if (curr.cells[ci] !== orig.cells[ci]) colors++;
      }
    }

    // Duration changes
    for (let i = 0; i < data.phases.length; i++) {
      if (data.phases[i].duration !== originalData.phases[i]?.duration) durations++;
    }

    // Metadata changes
    const metaKeys: (keyof DiagramMetadata)[] = ['localitat', 'cruilla', 'projecte', 'data', 'regulador', 'planol', 'modificat'];
    for (const key of metaKeys) {
      if (data.metadata[key] !== originalData.metadata[key]) metadata++;
    }

    return { colors, durations, metadata, total: colors + durations + metadata };
  }, [data, originalData]);

  const handleReset = useCallback(() => {
    if (originalData) {
      setData(structuredClone(originalData));
    }
  }, [originalData]);

  // --- Export TXT ---

  const handleExportTxt = useCallback(() => {
    if (!data?.rawTxt) return;
    try {
      const txt = serializeToArsTxt(data);
      const blob = new Blob([txt], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.rawTxt.filename || 'export.TXT';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error exportando TXT');
    }
  }, [data]);

  // --- Generate PDF ---

  const handleGeneratePdf = useCallback(async () => {
    if (!data) return;
    setPdfLoading(true);
    try {
      // Si useRawOffset, enviar rawOffset como offset principal al PDF
      const pdfData = useRawOffset
        ? { ...data, plans: data.plans.map(p => ({ ...p, offset: p.rawOffset })) }
        : data;
      const res = await fetch(`${PDF_SERVICE_URL}/generate-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ diagram_data: pdfData, landscape, max_groups: maxGroups }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Error del servidor: ${res.status} - ${text}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `diagrama-${data.metadata.cruilla || 'export'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error generando PDF');
    } finally {
      setPdfLoading(false);
    }
  }, [data, useRawOffset, landscape, maxGroups]);

  const handleClear = useCallback(() => {
    setData(null);
    setOriginalData(null);
    setError(null);
    setFileName('');
    setSourceType(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {!data ? (
        /* Estado sin archivo: zona de drop */
        <div className="flex-1 flex items-center justify-center p-8">
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`
              w-full max-w-lg p-12 rounded-xl border-2 border-dashed
              transition-all cursor-pointer text-center
              ${isDragging
                ? 'border-green-500 bg-green-500/10'
                : 'border-gray-700 bg-gray-900/50 hover:border-gray-500 hover:bg-gray-900'}
            `}
          >
            <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-green-400' : 'text-gray-500'}`} />
            <h2 className="text-lg font-medium text-gray-300 mb-2">
              Cargar diagrama ARS_93
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Arrastra un archivo <code className="text-gray-400">.TXT</code> de configuración ARS_93
              o haz click para seleccionar
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.html,.htm"
              onChange={handleInputChange}
              className="hidden"
            />
            {error && (
              <div className="mt-4 flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Estado con datos: preview + acciones */
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Toolbar */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gray-900/50 border-b border-gray-800 flex-shrink-0">
            <FileText className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-300">{fileName}</span>
            {data.metadata.cruilla && (
              <span className="text-xs px-2 py-0.5 bg-green-900/50 text-green-400 rounded">
                Cruce {data.metadata.cruilla}
              </span>
            )}
            {changeCount.total > 0 && (
              <div className="flex items-center gap-2">
                <span
                  className="text-xs px-2 py-0.5 bg-orange-900/40 text-orange-400 rounded cursor-default"
                  title={[
                    changeCount.colors > 0 && `${changeCount.colors} color${changeCount.colors > 1 ? 's' : ''}`,
                    changeCount.durations > 0 && `${changeCount.durations} duraci${changeCount.durations > 1 ? 'ones' : 'on'}`,
                    changeCount.metadata > 0 && `${changeCount.metadata} camp${changeCount.metadata > 1 ? 'os' : 'o'}`,
                  ].filter(Boolean).join(', ')}
                >
                  {changeCount.total} cambio{changeCount.total > 1 ? 's' : ''}
                </span>
                <button
                  onClick={handleReset}
                  className="flex items-center gap-1 px-2 py-0.5 rounded text-xs text-orange-400 hover:text-orange-300 hover:bg-orange-900/30 transition-colors"
                  title="Restaurar al original"
                >
                  <RotateCcw className="w-3 h-3" />
                  Reset
                </button>
              </div>
            )}
            <div className="flex-1" />
            {data.plans.length > 0 && data.plans.some(p => p.rawOffset !== p.offset) && (
              <button
                onClick={() => setUseRawOffset(r => !r)}
                className={`
                  flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                  transition-colors border
                  ${useRawOffset
                    ? 'border-amber-600 bg-amber-900/30 text-amber-400'
                    : 'border-gray-700 bg-gray-800 text-gray-400 hover:text-gray-300'}
                `}
                title={useRawOffset
                  ? 'Dsf. original del fichero TXT (inicio de F1)'
                  : 'Dsf. calculado (final de F1)'}
              >
                Dsf: {useRawOffset ? 'Original' : 'Calculado'}
              </button>
            )}
            <button
              onClick={() => setMaxGroups(g => g === 16 ? 32 : 16)}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                transition-colors border
                ${maxGroups === 32
                  ? 'border-purple-600 bg-purple-900/30 text-purple-400'
                  : 'border-gray-700 bg-gray-800 text-gray-400 hover:text-gray-300'}
              `}
              title={`PDF con ${maxGroups} grupos`}
            >
              {maxGroups}G
            </button>
            <button
              onClick={() => setLandscape(l => !l)}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                transition-colors border
                ${landscape
                  ? 'border-blue-600 bg-blue-900/30 text-blue-400'
                  : 'border-gray-700 bg-gray-800 text-gray-400 hover:text-gray-300'}
              `}
              title={landscape ? 'PDF en formato horizontal' : 'PDF en formato vertical'}
            >
              {landscape
                ? <><RectangleHorizontal className="w-3.5 h-3.5" /> Horitzontal</>
                : <><RectangleVertical className="w-3.5 h-3.5" /> Vertical</>
              }
            </button>
            {sourceType === 'txt' && data.rawTxt && (
              <button
                onClick={handleExportTxt}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-emerald-600 text-white hover:bg-emerald-500"
                title="Exportar TXT ARS_93"
              >
                <FileDown className="w-4 h-4" />
                Exportar TXT
              </button>
            )}
            <button
              onClick={handleGeneratePdf}
              disabled={pdfLoading}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                transition-colors
                ${pdfLoading
                  ? 'bg-gray-700 text-gray-400 cursor-wait'
                  : 'bg-blue-600 text-white hover:bg-blue-500'}
              `}
            >
              <Download className="w-4 h-4" />
              {pdfLoading ? 'Generando...' : 'Generar PDF'}
            </button>
            <button
              onClick={handleClear}
              className="p-2 text-gray-400 hover:text-white transition-colors"
              title="Cerrar diagrama"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {error && (
            <div className="px-4 py-2 bg-red-900/30 border-b border-red-800 flex items-center gap-2 text-red-400 text-sm flex-shrink-0">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Panel lateral + Preview del diagrama */}
          <div className="flex-1 flex overflow-hidden">
            <BarDiagramSidePanel
              data={data}
              collapsed={panelCollapsed}
              onToggle={() => setPanelCollapsed(!panelCollapsed)}
              onActivePhaseChange={setActivePhaseIndex}
            />
            <div className="flex-1 overflow-auto p-6">
              <div
                className="mx-auto rounded-lg shadow-lg p-6"
                style={{
                  backgroundColor: 'white',
                  maxWidth: 1100,
                }}
              >
                <h2
                  style={{
                    fontFamily: 'Arial Rounded MT Bold, Helvetica Rounded, Arial, sans-serif',
                    fontSize: 14,
                    textAlign: 'center',
                    marginBottom: 16,
                    color: 'black',
                  }}
                >
                  DIAGRAMA BARRAS: {data.metadata.cruilla}
                </h2>

                <BarDiagramChart
                  data={data}
                  activePhaseIndex={activePhaseIndex}
                  useRawOffset={useRawOffset}
                  editable
                  onCellColorChange={handleCellColorChange}
                  onDurationChange={handleDurationChange}
                />
                <BarDiagramInfo
                  metadata={data.metadata}
                  editable
                  onMetadataChange={handleMetadataChange}
                />
              </div>
            </div>
            {originalData && changeCount.total > 0 && (
              <BarDiagramChangesPanel
                data={data}
                originalData={originalData}
                collapsed={changesPanelCollapsed}
                onToggle={() => setChangesPanelCollapsed(c => !c)}
                onReset={handleReset}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
